import store from '@/store'
import jwt from '@/auth/jwt/useJwt'
import router from '@/router'
import { serviceOptions, pointOwnerOptions, locales } from '@/mixins/options'
import i18n from '@/libs/i18n'
import { matchPurposes as svcMatch, listPurposes as svcList } from '@/services/pps/purposesClient'

import { addPendingAlias } from '@/utils/location/pendingLocations'
import { useAppInstallation } from '@/utils/useAppInstallation'
import { storage } from '@/utils/storage'
import { computed } from '@vue/composition-api'

import {
  applyRequestMeta,
  resetRequestMeta,
} from '@/utils/meta/requestMeta'

const USE_SVC_LIST = process.env.VUE_APP_PURPOSES_SVC_LIST === '1'
// tiny normalizer for params
function normParams(input = {}) {
  const p = { ...input }
  if (typeof p.q === 'string') p.q = p.q.trim()
  if (typeof p.lang === 'string') p.lang = p.lang.trim()
  if (p.limit != null) p.limit = Number(p.limit) || 40
  return p
}
export const usePointRemoteData = () => {
  const closeServiceRequest = (...args) => store.dispatch('point/closeServiceRequest', ...args)
  // const fetchPointPurposes = (...args) => store.dispatch('point/fetchPointPurposes', ...args)
  /**
   * fetchPointPurposes(params)
   * Keeps the old backend behavior via Vuex.
   * If USE_SVC_LIST=1 -> use microservice by default, fallback to backend.
   * Otherwise -> use backend by default, fallback to microservice.
   * Always returns an object shaped like Axios { data: { data: [...] } }.
   */
  const fetchPointPurposes = async (params = {}) => {
    const normalized = normParams(params)

    const callBackend = async () =>
      // shape: { data: { data: [...] } }
      store.dispatch('point/fetchPointPurposes', normalized)

    const callService = async () => {
      // Map backend-style param -> service-style
      const svcParams = { ...normalized }
      if (svcParams.point_type_id != null) {
        svcParams.point_type_ids = String(svcParams.point_type_id)
        delete svcParams.point_type_id
      }

      // svcList returns { data: [...] }
      const payload = await svcList(svcParams)

      // always return backend-like shape
      const items = Array.isArray(payload?.data) ? payload.data : []
      return { data: { data: items } }
    }

    if (USE_SVC_LIST) {
      try {
        return await callService()
      } catch (e) {
        return await callBackend()
      }
    } else {
      try {
        return await callBackend()
      } catch (e) {
        return await callService()
      }
    }
  }

  const captureServiceRequest = (...args) => store.dispatch('point/captureServiceRequest', ...args)

  /** Matcher: always use the microservice */
  const matchPurposes = payload => svcMatch(payload) // returns { data: [...] }
  return {
    closeServiceRequest,
    fetchPointPurposes,
    matchPurposes,
    captureServiceRequest,
  }
}

export const useEcommerce = () => {
  // eslint-disable-next-line arrow-body-style
  const addProductInWishlist = productId => {
    return store.dispatch('app-ecommerce/addProductInWishlist', { productId })
  }

  // eslint-disable-next-line arrow-body-style
  const removeProductFromWishlist = productId => {
    return store.dispatch('app-ecommerce/removeProductFromWishlist', { productId })
  }

  // eslint-disable-next-line arrow-body-style
  const addProductInCart = productId => {
    return store.dispatch('app-ecommerce/addProductInCart', { productId })
  }

  // eslint-disable-next-line arrow-body-style
  const removeProductFromCart = productId => {
    return store.dispatch('app-ecommerce/removeProductFromCart', { productId })
  }

  return {
    addProductInWishlist,
    removeProductFromWishlist,
    addProductInCart,
    removeProductFromCart,
  }
}

export const usePointsUi = () => {
  const OWNER_BY_SLUG = pointOwnerOptions.reduce((acc, o) => { acc[o.slug] = o; return acc }, Object.create(null))
  const DEFAULT_OWNER = OWNER_BY_SLUG.own
  function getOwnerProperty(pointData) {
    // tolerate missing store during early renders
    const src = pointData
      ?? (typeof store !== 'undefined' && store.getters && store.getters['point/Point'])
      ?? {}

    const managed = Boolean(src.managed)

    const hasManager = src.manager_id != null
      && String(src.manager_id).trim() !== ''
      && Number(src.manager_id) > 0

    const slug = hasManager
      ? (managed ? 'onBehalfManaged' : 'onBehalf')
      : 'own'

    const found = OWNER_BY_SLUG[slug] || DEFAULT_OWNER

    // Ensure safe fallback values
    return {
      icon: (found && found.icon) || 'UserIcon',
      class: (found && found.class) || 'light-secondary',
      ...found,
    }
  }

  const userCanAddOffer = () => {
    const user = store.getters['account/user']

    if (!user.id) return true

    let userAbilities = user.ability

    if (!userAbilities && user.id) return false

    const { subscriptions } = user

    if (subscriptions && subscriptions.length) {
      const subscriptionAbilities = subscriptions.map(el => el.abilities).shift()

      if (subscriptionAbilities && subscriptionAbilities.length) {
        userAbilities = [...userAbilities, ...subscriptionAbilities]
      }
    }
    const createOfferAbilityIndex = userAbilities.findIndex(el => el.subject === 'ServiceOffer' && el.action === 'create')

    return (createOfferAbilityIndex > -1)
  }

  const mainImage = point => point.main_image && 'url' in point.main_image

  const defaultImage = require('@/assets/images/svg/service-noimage.svg')

  const getMainImage = point => (mainImage(point) ? point.main_image.url : defaultImage)

  const getFirstGalleryImage = point => (point.media_files !== undefined && point.media_files.length ? point.media_files[0].url : defaultImage)

  const getOptionPurposes = options => {
    // Add icons, tooltips etc
    const result = []
    options.forEach(obj => {
      const purpose = serviceOptions.purposes.find(({ slug }) => slug === obj.slug)
      result.push({ ...purpose, ...obj })
    })
    return result
  }

  const resolvePointTypeBadgeVariant = pointTypeSlug => (pointTypeSlug === 'offer' ? 'warning' : 'primary')
  const resolveCloseReasonColorVariant = pointCloseReason => (pointCloseReason === 'request_completed' ? 'success' : 'secondary')

  const isManaged = point => point.managed
  const isOnBehalf = point => point.managed !== 'undefined'

  const toggleProductInWishlist = product => {
    const { addProductInWishlist, removeProductFromWishlist } = useEcommerce()
    if (product.isInWishlist) {
      removeProductFromWishlist(product.id).then(() => {
        // eslint-disable-next-line no-param-reassign
        product.isInWishlist = false
      })
    } else {
      addProductInWishlist(product.id).then(() => {
        // eslint-disable-next-line no-param-reassign
        product.isInWishlist = true
      })
    }
  }

  const handleCartActionClick = product => {
    const { addProductInCart } = useEcommerce()

    if (product.isInCart) {
      router.push({ name: 'apps-e-commerce-checkout' })
    } else {
      addProductInCart(product.id).then(() => {
        // eslint-disable-next-line no-param-reassign
        product.isInCart = true

        // Update cart items count
        store.commit('app-ecommerce/UPDATE_CART_ITEMS_COUNT', store.state['app-ecommerce'].cartItemsCount + 1)
      })
    }
  }

  const handleEditOfferActionClick = product => {
    router.push({ name: 'apps-service-offer-edit', params: { id: product.id } })
  }
  const handleEditRequestActionClick = product => {
    router.push({ name: 'apps-service-request-edit', params: { id: product.id } })
  }
  const handleWishlistCartActionClick = (product, removeProductFromWishlistUi) => {
    const { addProductInCart, removeProductFromWishlist } = useEcommerce()

    if (product.isInCart) {
      router.push({ name: 'apps-e-commerce-checkout' })
    } else {
      addProductInCart(product.id)
        .then(() => {
          // eslint-disable-next-line no-param-reassign
          product.isInCart = true
          removeProductFromWishlist(product.id)

          // Update cart items count
          store.commit('app-ecommerce/UPDATE_CART_ITEMS_COUNT', store.state['app-ecommerce'].cartItemsCount + 1)
        })
        .then(() => {
          // eslint-disable-next-line no-param-reassign
          product.isInWishlist = false
          removeProductFromWishlistUi(product)
        })
    }
  }
  return {
    toggleProductInWishlist,
    handleCartActionClick,
    handleEditOfferActionClick,
    handleEditRequestActionClick,
    handleWishlistCartActionClick,
    resolvePointTypeBadgeVariant,
    resolveCloseReasonColorVariant,
    getOptionPurposes,
    mainImage,
    getMainImage,
    getFirstGalleryImage,
    defaultImage,
    userCanAddOffer,
    isManaged,
    isOnBehalf,
    getOwnerProperty,
  }
}

export const useServicesQueryHandler = () => {
  const {
    platform, hasBeenDismissed, hasAttemptedThisSession, openAppOrStore,
  } = useAppInstallation()

  // Constants
  const SERVICE_QUERY_PAGE_KEY = 'q'
  const SERVICE_LOCATION_REQUEST_KEY = 'slr'
  const SERVICE_REFERRAL_KEY = 'rc'
  const SERVICE_PURPOSES_KEY = 'pids'
  const SERVICE_LANG_KEY = 'lang'
  const SERVICE_USER_ID_KEY = 'uid'
  const REDIRECT_AFTER_LOGIN_KEY = 'redirect_to_after_login'
  const SERVICE_REQUEST_ID_KEY = 'rid'

  /**
   * Utility: does current query contain a "service intent" we should handle?
   */
  const hasServiceIntent = q => Boolean(
    q?.[SERVICE_LOCATION_REQUEST_KEY]
    || q?.[SERVICE_USER_ID_KEY]
    || q?.[SERVICE_REFERRAL_KEY]
    || q?.[SERVICE_PURPOSES_KEY]
    || q?.[SERVICE_REQUEST_ID_KEY],
  )

  /**
   * Utility: safely parse user id
   */
  const parseUserId = value => {
    const n = Number.parseInt(String(value), 10)
    return Number.isFinite(n) ? n : null
  }

  /**
   * Utility: consume stored redirect once, with loop protection.
   * - We remove the stored redirect before navigating to avoid bouncing.
   * - We also avoid navigating if it's the same as current fullPath.
   */
  const tryConsumeStoredRedirect = async () => {
    const stored = storage.getItem(REDIRECT_AFTER_LOGIN_KEY)
    if (!stored) return false

    const currentFullPath = router.currentRoute?.fullPath
      || router.currentRoute?.value?.fullPath
      || ''

    // Prevent trivial loops
    if (stored === currentFullPath) {
      storage.removeItem(REDIRECT_AFTER_LOGIN_KEY)
      return false
    }

    storage.removeItem(REDIRECT_AFTER_LOGIN_KEY)
    await router.push(stored)
    return true
  }
  const INSTALL_PROMPT_SESSION_KEY = 'otj_show_install_prompt'

  const setInstallPromptThisSession = () => {
    try { sessionStorage.setItem(INSTALL_PROMPT_SESSION_KEY, '1') } catch (_) {}
  }

  const shouldShowInstallPrompt = () => {
    if (hasBeenDismissed()) return false
    try { return sessionStorage.getItem(INSTALL_PROMPT_SESSION_KEY) === '1' } catch (_) { return false }
  }

  const dismissInstallPromptForSession = () => {
    try { sessionStorage.removeItem(INSTALL_PROMPT_SESSION_KEY) } catch (_) {}
  }
  /**
   * Main handler to process incoming query parameters.
   * Rules:
   * - Persist lang/rc/pids ALWAYS (independent from uid/slr).
   * - Intent priority: uid > slr.
   * - uid: always open provider profile page (never force landing). If not logged -> store redirect_to_after_login.
   * - slr: if not logged -> go landing and scroll to forms (login is there), store redirect_to_after_login to wizard.
   * - Optional Android install promo stays the same.
   * - Default: landing.
   */

  const serviceQueryHandler = async () => {
    // Vue Router v3/v4 compatibility
    const current = router.currentRoute?.value || router.currentRoute
    const q = current?.query || {}

    // 0) If no URL intent but redirect stored -> consume it
    // (do this BEFORE falling back anywhere)
    if (!hasServiceIntent(q)) {
      const consumed = await tryConsumeStoredRedirect()
      if (consumed) return
    }

    // 1) Language Handling
    if (SERVICE_LANG_KEY in q) {
      const { lang } = q
      const { setLocale, getLang } = locales()
      if (getLang(lang)) setLocale(lang)
    }

    // 2) Persist referral & purposes ALWAYS (no dependency on uid/slr)
    const queryData = {}

    if (SERVICE_REFERRAL_KEY in q) {
      storage.setItem(SERVICE_REFERRAL_KEY, q[SERVICE_REFERRAL_KEY])
    }

    if (SERVICE_PURPOSES_KEY in q) {
      queryData.point_purposes = String(q[SERVICE_PURPOSES_KEY])
        .split('&')
        .map(Number)
        .filter(n => Number.isFinite(n))
    }

    if (Object.keys(queryData).length > 0) {
      storage.set(SERVICE_QUERY_PAGE_KEY, queryData)
    }

    // 3) Resolve Target Route (PRIORITY: uid > slr)
    let targetRoute = null
    let intent = null // 'uid' | 'slr' | null

    // --- uid ---
    if (
      Object.prototype.hasOwnProperty.call(q, SERVICE_USER_ID_KEY)
      && q[SERVICE_USER_ID_KEY] != null
    ) {
      const userId = parseUserId(q[SERVICE_USER_ID_KEY])

      if (userId !== null) {
        intent = 'uid'
        targetRoute = {
          name: 'pages-profile-service-provider',
          params: { id: userId },
        }
      }
    }

    // --- rid (shared request link) ---
    else if (Object.prototype.hasOwnProperty.call(q, SERVICE_REQUEST_ID_KEY)) {
      const rawRid = q[SERVICE_REQUEST_ID_KEY]
      const ridValue = Array.isArray(rawRid) ? rawRid[0] : rawRid

      const identifier = typeof ridValue === 'string'
        ? ridValue.trim()
        : String(ridValue || '').trim()

      if (identifier) {
        intent = 'rid'
        targetRoute = {
          name: 'apps-service-request-public-info',
          params: { id: identifier },
        }
      }
    }

    // --- slr ---
    else if (Object.prototype.hasOwnProperty.call(q, SERVICE_LOCATION_REQUEST_KEY)) {
      const rawSlr = q[SERVICE_LOCATION_REQUEST_KEY]

      // Normalize possible array / non-string cases (router can give arrays)
      const slrValue = Array.isArray(rawSlr) ? rawSlr[0] : rawSlr

      const alias = typeof slrValue === 'string'
        ? slrValue.trim()
        : String(slrValue || '').trim()

      // Treat empty as "no intent"
      if (alias) {
        intent = 'slr'

        // Save alias for LocationBlock.applyLocationFromAlias()
        // Only store real alias (not just boolean flag)
        if (alias !== '1' && alias.toLowerCase() !== 'true') {
          addPendingAlias(alias)
        }

        targetRoute = { name: 'apps-service-request-add-wizard' }
      }
    }

    // No target intent -> landing (rc/lang/pids already persisted above)
    if (!targetRoute) {
      return router.push({ name: 'pages-landing' })
    }

    // Keep your current "logged" meaning:
    // you used access token only here; preserve it to avoid behavior changes.
    const isLogged = !!jwt.getToken() || !!jwt.getRefreshToken()

    // Resolve path lazily (needed only if we store redirect)
    const resolved = router.resolve(targetRoute)
    const targetPath = (resolved && resolved.route && resolved.route.fullPath)
      ? resolved.route.fullPath
      : resolved.href

    // 4) Android: attempt open/store once per session.
    // If user returns without installing => allow web flow but show prompt (unless dismissed).
    // if (intent && platform.isAndroidBrowser && !platform.isAndroidWebView && !window.OneTimeJobBridge) {
    //   storage.setItem(REDIRECT_AFTER_LOGIN_KEY, targetPath)
    //
    //   // If we already tried to open app / go to Play in this session, don't bounce again.
    //   // Instead, allow web flow and show the install prompt (unless user dismissed it).
    //   if (hasAttemptedThisSession()) {
    //     setInstallPromptThisSession()
    //   } else {
    //     const res = await openAppOrStore(window.location.href, { delayMs: 900 })
    //     if (res?.didAttempt) return
    //   }
    // }

    // 5) Navigate (preserve behavior)

    if (intent === 'uid') {
      // Always open provider profile page.
      // If not logged, just remember where to come back after login
      if (!isLogged) {
        storage.setItem(REDIRECT_AFTER_LOGIN_KEY, targetPath)
      }

      return router.push(targetRoute)
    }

    if (intent === 'rid') {
      return router.push(targetRoute)
    }

    if (intent === 'slr') {
      // Wizard intent: better UX to show landing forms if not logged
      if (!isLogged) {
        storage.setItem(REDIRECT_AFTER_LOGIN_KEY, targetPath)

        return router.push({
          name: 'pages-landing',
          query: {
            scroll: 'forms',
            role: 'customer',
            ...(q[SERVICE_LANG_KEY]
              ? { [SERVICE_LANG_KEY]: q[SERVICE_LANG_KEY] }
              : {}),
          },
        })
      }

      return router.push(targetRoute)
    }

    // Safety fallback
    return router.push({ name: 'pages-landing' })
  }

  // Helper functions...
  const getServiceRequestPublicLink = identifier => {
    const requestIdentifier = identifier !== undefined && identifier !== null
      ? String(identifier).trim()
      : ''

    if (!requestIdentifier) {
      return ''
    }

    const url = new URL(`${window.location.origin}/${SERVICE_QUERY_PAGE_KEY}`)
    url.searchParams.append(SERVICE_REQUEST_ID_KEY, requestIdentifier)
    url.searchParams.append(SERVICE_LANG_KEY, i18n.locale)

    return url.href
  }

  const getLinkToQueryPage = () => {
    const url = new URL(`${window.location.origin}/${SERVICE_QUERY_PAGE_KEY}`)
    url.searchParams.append(SERVICE_LANG_KEY, i18n.locale)
    return url.href
  }

  const getServiceQueryLink = () => {
    const filter = store.getters['point/filter']
    const props = router.resolve({
      name: 'app-service-query',
      query: {
        tid: filter.point_type || '',
        [SERVICE_PURPOSES_KEY]: filter.point_purposes
          ? filter.point_purposes.join('&')
          : '',
        [SERVICE_LANG_KEY]: filter.languages,
      },
    })
    return props ? window.location.origin + props.href : window.location.origin
  }

  return {
    serviceQueryHandler,
    getServiceQueryLink,
    getLinkToQueryPage,
    getServiceRequestPublicLink,
    getQuery: () => storage.get(SERVICE_QUERY_PAGE_KEY),
    setQuery: (key, value) => storage.setItem(key, value),
    removeQuery: () => storage.removeItem(SERVICE_QUERY_PAGE_KEY),
    SERVICE_QUERY_PAGE_KEY,
    SERVICE_LOCATION_REQUEST_KEY,
    SERVICE_REFERRAL_KEY,
    SERVICE_PURPOSES_KEY,
    SERVICE_LANG_KEY,
    SERVICE_USER_ID_KEY,
    REDIRECT_AFTER_LOGIN_KEY,
    SERVICE_REQUEST_ID_KEY,
    shouldShowInstallPrompt,
    dismissInstallPromptForSession,
    setInstallPromptThisSession,
  }
}

export const useServiceMode = () => {
// serviceMode.constants.js

  const SERVICE_MODES = {
    OFFICE_AND_MOBILE: 'office_and_mobile',
    OFFICE_ONLY: 'office_only',
    MOBILE_ONLY: 'mobile_only',
    UNKNOWN: 'unknown',
  }

  const SERVICE_MODE_FLAGS = {
    [SERVICE_MODES.OFFICE_AND_MOBILE]: { in_office: true, on_site: true },
    [SERVICE_MODES.OFFICE_ONLY]: { in_office: true, on_site: false },
    [SERVICE_MODES.MOBILE_ONLY]: { in_office: false, on_site: true },
  }

  // Basic flags → mode (no hasOffice fallback)
  // used by the EDITOR
  function inferServiceModeFromFlags(inOffice, onSite) {
    const hasIn = typeof inOffice !== 'undefined'
    const hasOn = typeof onSite !== 'undefined'

    if (!hasIn && !hasOn) return SERVICE_MODES.UNKNOWN

    const inB = !!inOffice
    const onB = !!onSite

    if (inB && onB) return SERVICE_MODES.OFFICE_AND_MOBILE
    if (inB && !onB) return SERVICE_MODES.OFFICE_ONLY
    if (!inB && onB) return SERVICE_MODES.MOBILE_ONLY

    return SERVICE_MODES.UNKNOWN
  }

  // Display-only helper: adds hasOffice heuristic
  function inferServiceModeWithOffice(inOffice, onSite, hasOffice) {
    const base = inferServiceModeFromFlags(inOffice, onSite)
    if (base !== SERVICE_MODES.UNKNOWN) return base

    const hasIn = typeof inOffice !== 'undefined'
    const hasOn = typeof onSite !== 'undefined'

    if (!hasIn && !hasOn) {
      if (hasOffice === true) return SERVICE_MODES.OFFICE_AND_MOBILE
      if (hasOffice === false) return SERVICE_MODES.MOBILE_ONLY
    }

    return SERVICE_MODES.UNKNOWN
  }

  return {
    SERVICE_MODES,
    SERVICE_MODE_FLAGS,
    inferServiceModeFromFlags,
  }
}

const SERVICE_TYPE_ICON_MAP = {
  home_master: 'ToolIcon',
  vehicle_repair: 'TruckIcon',
  renovation: 'HomeIcon',
  labourers: 'UsersIcon',
  equipment_repair: 'CpuIcon',
  delivery: 'PackageIcon',
  moving: 'TruckIcon',
  cleaning: 'DropletIcon',
  sport: 'ActivityIcon',
  beauty: 'HeartIcon',
  animals: 'SmileIcon',
  holidays: 'CalendarIcon',
  business: 'BriefcaseIcon',
  media: 'CameraIcon',
  education: 'BookOpenIcon',
  household: 'ArchiveIcon',
  medicine: 'PlusCircleIcon',
  relocation: 'MapIcon',
  entertainment: 'MusicIcon',
  handicraft: 'ScissorsIcon',
  rental: 'KeyIcon',
  food: 'CoffeeIcon',
  climate: 'WindIcon',
  advertising: 'TargetIcon',
}

const DEFAULT_ICON = 'FileTextIcon'

export const resolveServiceTypeIcon = slug => {
  const normalized = String(slug || '').trim().toLowerCase()
  return SERVICE_TYPE_ICON_MAP[normalized] || DEFAULT_ICON
}

export const resolveServiceIconFromItem = item => resolveServiceTypeIcon(item?.point_type?.slug)

export default function useServiceTypes() {
  const serviceTypeIconMap = computed(() => SERVICE_TYPE_ICON_MAP)

  return {
    serviceTypeIconMap,
    resolveServiceTypeIcon,
    resolveServiceIconFromItem,
  }
}

export const usePointMeta = () => {
  const { getServiceRequestPublicLink } = useServicesQueryHandler()

  const normalizePointAddress = point => {
    if (!point || !point.address) {
      return {}
    }

    if (point.address.address && typeof point.address.address === 'object') {
      return point.address.address
    }

    return point.address
  }

  const getPointLocationParts = point => {
    const address = normalizePointAddress(point)

    const city = address.city || address.locality || address.town || ''
    const country = address.country || ''

    return [city, country].filter(Boolean)
  }

  const getPointLocationLabel = point => getPointLocationParts(point).join(', ')

  const getPointMetaTitle = point => {
    const baseTitle = point && (point.title || point.name)
      ? (point.title || point.name)
      : i18n.t('request.meta.default_title')

    const location = getPointLocationLabel(point)

    return location ? `${baseTitle} — ${location}` : baseTitle
  }

  const getPointMetaDescription = point => {
    const baseDescription = point && point.description
      ? String(point.description).replace(/\s+/g, ' ').trim()
      : (point && (point.title || point.name))
        ? String(point.title || point.name)
        : i18n.t('request.meta.default_description')

    const location = getPointLocationLabel(point)
    const description = location
      ? `${baseDescription}. ${i18n.t('common.location')}: ${location}.`
      : baseDescription

    return description.slice(0, 180)
  }

  const getPointMetaImage = point => {
    if (point && point.main_image && point.main_image.url) {
      return point.main_image.url
    }

    return ''
  }

  const getPointMetaUrl = point => {
    const identifier = point && (point.public_alias || point.id)
      ? (point.public_alias || point.id)
      : ''

    return getServiceRequestPublicLink(identifier)
  }

  const updateMetaFromPoint = point => {
    if (!point) {
      return
    }

    applyRequestMeta({
      title: getPointMetaTitle(point),
      description: getPointMetaDescription(point),
      image: getPointMetaImage(point),
      url: getPointMetaUrl(point),
    })
  }

  const clearPointMeta = () => {
    resetRequestMeta()
  }

  return {
    clearPointMeta,
    getPointLocationLabel,
    getPointMetaDescription,
    getPointMetaImage,
    getPointMetaTitle,
    getPointMetaUrl,
    normalizePointAddress,
    updateMetaFromPoint,
  }
}
